import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { applicationUrls } from 'src/app/shared/application-constants/application-urls.const';
import { ServerResponse } from 'src/app/shared/models/dto/server-response.dto';
import { STPMain } from '../../../models/stp_main.model';
import { map, tap } from 'rxjs/operators';
import { StpTrainer } from '../../../models/stp-trainer.model';
import { StpManagement } from '../../../models/stp-management.model';
import { StpOccupationTool } from '../../../models/stp-occupation-tool.model';
import { Furniture } from '../../../models/furniture.model';
import { SaveInspectionDecisionDto } from 'src/app/modules/course-accreditation/DTO/saveInspectionDecisionDto';

@Injectable({
  providedIn: 'root',
})
export class StpRegistrationRenewReapplyService {
  stpTrainers: StpTrainer[] = [];
  onRefreshStpTrainerList = new Subject<StpTrainer[]>();
  onStartStpTrainerEditing = new Subject<number | null>();
  editingStpTrainerId!: number | null;

  stpManagements: StpManagement[] = [];
  onRefreshStpManagementList = new Subject<StpManagement[]>();
  onStartStpManagementEditing = new Subject<number | null>();
  editingStpManagementId!: number | null;

  stpOccupationTools: StpOccupationTool[] = [];
  onRefreshStpOccupationToolList = new Subject<StpOccupationTool[]>();
  onStartStpOccupationToolEditing = new Subject<number | null>();
  editingStpOccupationToolId!: number | null;

  furnitures: Furniture[] = [];
  onRefreshFurnitureList = new Subject<Furniture[]>();
  onStartFurnitureEditing = new Subject<number | null>();
  editingFurnitureId!: number | null;

  constructor(private httpClient: HttpClient) {}

  getStpMainAndCommitteeDumpInfoByStpMainId(
    id: number,
  ): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.getStpMainAndCommitteeDumpInfoByStpMainId}` +
        id,
    );
  }

  updateStpMainDumpBasicProfileInfo(
    stpMain: STPMain,
  ): Observable<ServerResponse> {
    return this.httpClient.put<ServerResponse>(
      `${applicationUrls.stpMainDump.updateStpMainDumpBasicProfileInfo}`,
      stpMain,
    );
  }

  getStpInfrastructureDumpInfoByStpMainId(
    id: number,
  ): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.getStpInfrastructureDumpInfoByStpMainId}` +
        id,
    );
  }

  updateStpMainDumpInfrastructureInfo(
    physicalInfrastructureRequest: any,
  ): Observable<any> {
    return this.httpClient.put<ServerResponse>(
      `${applicationUrls.stpMainDump.updateStpMainDumpInfrastructureInfo}`,
      physicalInfrastructureRequest,
    );
  }

  getStpTrainerDumpInfoByStpMainId(id: number): Observable<ServerResponse> {
    return this.httpClient
      .get<ServerResponse>(
        `${applicationUrls.stpMainDump.getStpTrainerDumpInfoByStpMainId}` + id,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.setStpTrainers(response?.data?.stpTrainerList);
          }
        }),
      );
  }

  setStpTrainers(stpTrainers: StpTrainer[]): void {
    this.stpTrainers = stpTrainers;
    this.onRefreshStpTrainerList.next(this.stpTrainers.slice());
  }

  getStpTrainers(): StpTrainer[] {
    return this.stpTrainers;
  }

  getStpTrainerById(id: number | null): StpTrainer | undefined {
    return this.stpTrainers.find((stpTrainer) => stpTrainer.id === id);
  }

  getEditingStpTrainerId(): any {
    return this.editingStpTrainerId;
  }

  setEditingStpTrainerId(id: number): void {
    this.editingStpTrainerId = id;
    this.onStartStpTrainerEditing.next(id);
  }

  saveStpMainDumpTrainerInfo(
    stpTrainer: StpTrainer,
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpTrainerInfo}`,
        stpTrainer,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.addStpTrainer(response?.data);
          }
        }),
      );
  }

  addStpTrainer(stpTrainer: StpTrainer): void {
    this.stpTrainers.push(stpTrainer);
    this.onRefreshStpTrainerList.next(this.stpTrainers.slice());
  }

  updateStpMainDumpTrainerInfo(
    stpTrainer: StpTrainer,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.updateStpMainDumpTrainerInfo}`,
        stpTrainer,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.updateStpTrainer(response?.data, response?.data?.id);
          }
        }),
      );
  }

  updateStpTrainer(newStpTrainer: StpTrainer, id: number): void {
    const index = this.stpTrainers.findIndex(
      (stpTrainer) => stpTrainer.id === id,
    );
    this.stpTrainers[index] = newStpTrainer;
    this.onRefreshStpTrainerList.next(this.stpTrainers.slice());
  }

  deleteStpMainDumpTrainerInfo(
    stpTrainer: StpTrainer,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.deleteStpMainDumpTrainerInfo}`,
        stpTrainer,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.deleteStpTrainer(response?.data?.id);
          }
        }),
      );
  }

  deleteStpTrainer(id: number): void {
    this.stpTrainers = this.stpTrainers.filter((stpTrainer, _index) => {
      return stpTrainer.id !== id;
    });
    this.onRefreshStpTrainerList.next(this.stpTrainers.slice());
  }

  saveStpMainDumpTrainerInfoListExcel(
    stpTrainerList: StpTrainer[],
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpTrainerInfoListExcel}`,
        stpTrainerList,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.setStpTrainersForExcel(response?.data);
          }
        }),
      );
  }

  setStpTrainersForExcel(stpTrainers: StpTrainer[]): void {
    this.stpTrainers = stpTrainers;
    this.onRefreshStpTrainerList.next(this.stpTrainers.slice());
  }

  getStpStaffDumpInfoByStpMainId(id: number): Observable<ServerResponse> {
    return this.httpClient
      .get<ServerResponse>(
        `${applicationUrls.stpMainDump.getStpStaffDumpInfoByStpMainId}` + id,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.setStpManagements(response?.data?.stpStaffList);
          }
        }),
      );
  }

  setStpManagements(stpManagements: StpManagement[]): void {
    this.stpManagements = stpManagements;
    this.onRefreshStpManagementList.next(this.stpManagements.slice());
  }

  getStpManagements(): StpManagement[] {
    return this.stpManagements;
  }

  getStpManagementById(id: number | null): StpManagement | undefined {
    return this.stpManagements.find((stpManagement) => stpManagement.id === id);
  }

  getEditingStpManagementId(): any {
    return this.editingStpManagementId;
  }

  setEditingStpManagementId(id: number): void {
    this.editingStpManagementId = id;
    this.onStartStpManagementEditing.next(id);
  }

  saveStpMainDumpStaffInfo(
    stpManagement: StpManagement,
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpStaffInfo}`,
        stpManagement,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.addStpManagement(response?.data);
          }
        }),
      );
  }

  addStpManagement(stpManagement: StpManagement): void {
    this.stpManagements.push(stpManagement);
    this.onRefreshStpManagementList.next(this.stpManagements.slice());
  }

  updateStpMainDumpStaffInfo(
    stpManagement: StpManagement,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.updateStpMainDumpStaffInfo}`,
        stpManagement,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.updateStpManagement(response?.data, response?.data?.id);
          }
        }),
      );
  }

  updateStpManagement(newStpManagement: StpManagement, id: number): void {
    const index = this.stpManagements.findIndex(
      (stpManagement) => stpManagement.id === id,
    );
    this.stpManagements[index] = newStpManagement;
    this.onRefreshStpManagementList.next(this.stpManagements.slice());
  }

  deleteStpMainDumpStaffInfo(
    stpManagement: StpManagement,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.deleteStpMainDumpStaffInfo}`,
        stpManagement,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.deleteStpManagement(response?.data?.id);
          }
        }),
      );
  }

  deleteStpManagement(id: number): void {
    this.stpManagements = this.stpManagements.filter(
      (stpManagement, _index) => {
        return stpManagement.id !== id;
      },
    );
    this.onRefreshStpManagementList.next(this.stpManagements.slice());
  }

  saveStpMainDumpStaffInfoListExcel(
    stpManagementList: StpManagement[],
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpStaffInfoListExcel}`,
        stpManagementList,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.setStpManagementsForExcel(response?.data);
          }
        }),
      );
  }

  setStpManagementsForExcel(stpManagements: StpManagement[]): void {
    this.stpManagements = stpManagements;
    this.onRefreshStpManagementList.next(this.stpManagements.slice());
  }

  getStpFinancialDetailsDumpInfoByStpMainId(
    id: number,
  ): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.getStpFinancialDetailsDumpInfoByStpMainId}` +
        id,
    );
  }

  updateStpMainDumpFinancialDetailsInfo(
    financialDetailsDumpRequest: any,
  ): Observable<any> {
    return this.httpClient.put<ServerResponse>(
      `${applicationUrls.stpMainDump.updateStpMainDumpFinancialDetailsInfo}`,
      financialDetailsDumpRequest,
    );
  }

  getStpToolsDumpInfoByStpMainId(id: number): Observable<ServerResponse> {
    return this.httpClient
      .get<ServerResponse>(
        `${applicationUrls.stpMainDump.getStpToolsDumpInfoByStpMainId}` + id,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.setStpOccupationTools(response?.data?.stpToolsList);
          }
        }),
      );
  }

  setStpOccupationTools(stpOccupationTools: StpOccupationTool[]): void {
    this.stpOccupationTools = stpOccupationTools;
    this.onRefreshStpOccupationToolList.next(this.stpOccupationTools.slice());
  }

  getStpOccupationTools(): StpOccupationTool[] {
    return this.stpOccupationTools;
  }

  getStpOccupationToolById(id: number | null): StpOccupationTool | undefined {
    return this.stpOccupationTools.find(
      (stpOccupationTool) => stpOccupationTool.id === id,
    );
  }

  getEditingStpOccupationToolId(): any {
    return this.editingStpOccupationToolId;
  }

  setEditingStpOccupationToolId(id: number): void {
    this.editingStpOccupationToolId = id;
    this.onStartStpOccupationToolEditing.next(id);
  }

  saveStpMainDumpToolsInfo(
    stpOccupationTool: StpOccupationTool,
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpToolsInfo}`,
        stpOccupationTool,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.addStpOccupationTool(response?.data);
          }
        }),
      );
  }

  addStpOccupationTool(stpOccupationTool: StpOccupationTool): void {
    this.stpOccupationTools.push(stpOccupationTool);
    this.onRefreshStpOccupationToolList.next(this.stpOccupationTools.slice());
  }

  updateStpMainDumpToolsInfo(
    stpOccupationTool: StpOccupationTool,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.updateStpMainDumpToolsInfo}`,
        stpOccupationTool,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.updateStpOccupationTool(response?.data, response?.data?.id);
          }
        }),
      );
  }

  updateStpOccupationTool(
    newStpOccupationTool: StpOccupationTool,
    id: number,
  ): void {
    const index = this.stpOccupationTools.findIndex(
      (stpOccupationTool) => stpOccupationTool.id === id,
    );
    this.stpOccupationTools[index] = newStpOccupationTool;
    this.onRefreshStpOccupationToolList.next(this.stpOccupationTools.slice());
  }

  deleteStpMainDumpToolsInfo(
    stpOccupationTool: StpOccupationTool,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.deleteStpMainDumpToolsInfo}`,
        stpOccupationTool,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.deleteStpOccupationTool(response?.data?.id);
          }
        }),
      );
  }

  deleteStpOccupationTool(id: number): void {
    this.stpOccupationTools = this.stpOccupationTools.filter(
      (stpOccupationTool, _index) => {
        return stpOccupationTool.id !== id;
      },
    );
    this.onRefreshStpOccupationToolList.next(this.stpOccupationTools.slice());
  }

  saveStpMainDumpToolsInfoListExcel(
    stpOccupationToolList: StpOccupationTool[],
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpToolsInfoListExcel}`,
        stpOccupationToolList,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.setStpOccupationToolsForExcel(response?.data);
          }
        }),
      );
  }

  setStpOccupationToolsForExcel(stpOccupationTools: StpOccupationTool[]): void {
    this.stpOccupationTools = stpOccupationTools;
    this.onRefreshStpOccupationToolList.next(this.stpOccupationTools.slice());
  }

  getStpFurnitureDumpInfoByStpMainId(id: number): Observable<ServerResponse> {
    return this.httpClient
      .get<ServerResponse>(
        `${applicationUrls.stpMainDump.getStpFurnitureDumpInfoByStpMainId}` +
          id,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.setFurniture(response?.data?.stpFurnitureList);
          }
        }),
      );
  }

  setFurniture(furnitures: Furniture[]): void {
    this.furnitures = furnitures;
    this.onRefreshFurnitureList.next(this.furnitures.slice());
  }

  getFurniture(): Furniture[] {
    return this.furnitures;
  }

  getFurnitureById(id: number | null): Furniture | undefined {
    return this.furnitures.find((furniture) => furniture.id === id);
  }

  getEditingFurnitureId(): any {
    return this.editingFurnitureId;
  }

  setEditingFurnitureId(id: number): void {
    this.editingFurnitureId = id;
    this.onStartFurnitureEditing.next(id);
  }

  saveStpMainDumpFurnitureInfo(
    furniture: Furniture,
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpFurnitureInfo}`,
        furniture,
      )
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.addFurniture(response?.data);
          }
        }),
      );
  }

  addFurniture(furniture: Furniture): void {
    this.furnitures.push(furniture);
    this.onRefreshFurnitureList.next(this.furnitures.slice());
  }

  updateStpMainDumpFurnitureInfo(
    furniture: Furniture,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.updateStpMainDumpFurnitureInfo}`,
        furniture,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.updateFurniture(response?.data, response?.data?.id);
          }
        }),
      );
  }

  updateFurniture(newFurniture: Furniture, id: number): void {
    const index = this.furnitures.findIndex((furniture) => furniture.id === id);
    this.furnitures[index] = newFurniture;
    this.onRefreshFurnitureList.next(this.furnitures.slice());
  }

  deleteStpMainDumpFurnitureInfo(
    furniture: Furniture,
  ): Observable<ServerResponse> {
    return this.httpClient
      .put<ServerResponse>(
        `${applicationUrls.stpMainDump.deleteStpMainDumpFurnitureInfo}`,
        furniture,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.deleteFurniture(response?.data?.id);
          }
        }),
      );
  }

  deleteFurniture(id: number): void {
    this.furnitures = this.furnitures.filter((furniture, _index) => {
      return furniture.id !== id;
    });
    this.onRefreshFurnitureList.next(this.furnitures.slice());
  }

  saveStpMainDumpFurnitureInfoListExcel(
    furnitureList: Furniture[],
  ): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpMainDump.saveStpMainDumpFurnitureInfoListExcel}`,
        furnitureList,
      )
      .pipe(
        tap((response) => {
          if (response?.success) {
            this.setFurnitureForExcel(response?.data);
          }
        }),
      );
  }

  setFurnitureForExcel(furnitures: Furniture[]): void {
    this.furnitures = furnitures;
    this.onRefreshFurnitureList.next(this.furnitures.slice());
  }

  getStpPaymentDumpInfoByStpMainId(id: number): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.getStpPaymentDumpInfoByStpMainId}` + id,
    );
  }

  checkStpMainDumpInfoByStpMainDumpId(id: number): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.checkStpMainDumpInfoByStpMainDumpId}` + id,
    );
  }

  getStpMainDumpInfoByStpMainDumpId(id: number): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.getStpMainDumpInfoByStpMainDumpId}` + id,
    );
  }

  getPaymentListByStpMainId(id: number): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.applications.getPaymentListByStpMainId}` + id,
    );
  }

  submitStpMainDumpInfoByStpMainDumpId(id: number): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.submitStpMainDumpInfoByStpMainDumpId}` +
        id,
    );
  }

  readInspectionScheduleInfo(data: any): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpRenewReapplyInspection.readInspectionScheduleDetails}`,
        data,
      )
      .pipe(
        map((serverResponse: ServerResponse) => {
          return serverResponse;
        }),
      );
  }

  saveInspectionSchedule(data: any): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpRenewReapplyInspection.saveInspectionSchedule}`,
        data,
      )
      .pipe(
        map((serverResponse: ServerResponse) => {
          return serverResponse;
        }),
      );
  }

  submitInspectionSchedule(inspectionId: number): Observable<ServerResponse> {
    return this.httpClient.put<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.submitInspectionSchedule}?inspectionId=${inspectionId}`,
      inspectionId
    );
  }

  getInspectionCompletionInfoByInspectionId(
    inspectionId: number,
  ): Observable<ServerResponse> {
    const params = new HttpParams().append('inspectionId', `${inspectionId}`);
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.getInspectionCompletionInformationByInspectionId}`,
      {
        params,
      },
    );
  }

  saveInspectionCompletionData(data: any): Observable<ServerResponse> {
    return this.httpClient
      .post<ServerResponse>(
        `${applicationUrls.stpRenewReapplyInspection.saveInspectionCompleteInformation}`,
        data,
      )
      .pipe(
        map((serverResponse: ServerResponse) => {
          return serverResponse;
        }),
      );
  }

  readStpInfoByStpMainId(stpMainId: number): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.readStpInfo}` + stpMainId,
    );
  }

  readStpAndSacRegInspectionReport(
    inspectionId: number,
  ): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.readStpSacRegInspectionReport}?inspectionId=${inspectionId}`,
    );
  }

  saveStpAndSacRegInspectionReport(
    inspectionReport: any,
    fileList: File[],
  ): Observable<ServerResponse> {
    let formData = new FormData();
    fileList.forEach((item: File, index: number) => {
      formData.append('file', item);
    });
    formData.append(
      'inspectionRegistrationRecords',
      JSON.stringify(inspectionReport.inspectionFormArray),
    );
    formData.append(
      'inspectionRegistration',
      JSON.stringify(inspectionReport.recommendation),
    );
    return this.httpClient.post<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.saveStpSacRegInspectionReport}`,
      formData,
    );
  }

  submitInspectionCompletionReport(
    inspectionId: number,
  ): Observable<ServerResponse> {
    return this.httpClient.put<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.submitInspectionCompletionReport}?inspectionId=${inspectionId}`,
      inspectionId,
    );
  }

  readResultScrutinyInfoByInspectionId(
    inspectionId: number,
  ): Observable<ServerResponse> {
    const params = new HttpParams().append('inspectionId', `${inspectionId}`);
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.getStpInspectionReportsRecommendationDetailsByInspectionId}`,
      {
        params,
      },
    );
  }

  updateStpSacResultScrutiny(
    stpSacScrutinyResultDto: any,
  ): Observable<ServerResponse> {
    return this.httpClient.put<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.updateStpSacResultScrutiny}`,
      stpSacScrutinyResultDto,
    );
  }

  rollbackStpFromResultScrutiny(
    inspectionId: number,
    updatedRemarks: string,
  ): Observable<ServerResponse> {
    const params = new HttpParams()
      .append('inspectionId', `${inspectionId}`)
      .append('updatedRemarks', updatedRemarks);

    return this.httpClient.put<ServerResponse>(
      applicationUrls.stpRenewReapplyInspection.rollbackStpFromResultScrutiny,
      {},
      { params },
    );
  }

  submitResultScrutiny(inspectionId: number): Observable<ServerResponse> {
    const url =
      applicationUrls.stpRenewReapplyInspection.submitStpResultScrutiny +
      '?inspectionId=' +
      inspectionId;
    return this.httpClient.post<ServerResponse>(url, {});
  }

  saveFinalDecision(
    saveInspectionDecisionDtoList: SaveInspectionDecisionDto[],
  ): Observable<ServerResponse> {
    const url = applicationUrls.stpRenewReapplyInspection.saveFinalDecision;
    return this.httpClient.post<ServerResponse>(
      url,
      saveInspectionDecisionDtoList,
    );
  }

  submitFinalDecision(inspectionId: number): Observable<ServerResponse> {
    const url =
      applicationUrls.stpRenewReapplyInspection.submitFinalDecision +
      '?inspectionId=' +
      inspectionId;
    return this.httpClient.post<ServerResponse>(url, {});
  }

  readDecisionDetailsByInspectionId(
    inspectionId: number,
  ): Observable<ServerResponse> {
    const params = new HttpParams().append('inspectionId', `${inspectionId}`);
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.getRenewReapplyDecisionDetailsByInspectionId}`,
      {
        params,
      },
    );
  }

  rollBackDecisionByInspectionId(
    inspectionId: number,
    upDatedRemarks: string,
  ): Observable<ServerResponse> {
    const params = new HttpParams()
      .append('inspectionId', `${inspectionId}`)
      .append('updatedRemarks', upDatedRemarks);

    return this.httpClient.put<ServerResponse>(
      applicationUrls.stpRenewReapplyInspection.rollbackStpFromDecision,
      {},
      { params },
    );
  }

  initiateRollBackRequest(
    stpMainDumpId: number,
    stpUserId: number,
    remarks: string,
  ): Observable<any> {
    return this.httpClient
      .get<ServerResponse>(
        `${
          applicationUrls.stpMainDump.renewReapplyRollBackRequest
        }?stpMainDumpId=${stpMainDumpId}&stpUserId=${stpUserId}&remarks=${remarks}`,
      )
      .pipe(
        map((serverResponse: any) => {
          return serverResponse;
        }),
      );
  }

  readStpRollbackHistory(stpMainDumpId: number): Observable<any> {
    return this.httpClient
      .get<ServerResponse>(
        `${applicationUrls.stpMainDump.readStpRenewReapplyRollbackHistory}` +
          stpMainDumpId,
      )
      .pipe(
        map((serverResponse: ServerResponse) => {
          if (!serverResponse.data) {
            return null;
          }
          return serverResponse;
        }),
      );
  }

  readStpExpirationDate(inspectionId: number): Observable<any> {
    return this.httpClient
      .get<ServerResponse>(
        `${applicationUrls.stpRenewReapplyInspection.readStpExpirationDate}` +
          inspectionId,
      )
      .pipe(
        map((serverResponse: ServerResponse) => {
          if (!serverResponse.data) {
            return null;
          }
          return serverResponse;
        }),
      );
  }

  getNextRenewReapplyPendingDecisionInspectionId() {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpRenewReapplyInspection.getNextRenewReapplyPendingDecisionInspectionId}`,
    );
  }

  getStpActionHistoryByStpMainId(
    stpMainId: number,
    applicationId: string,
  ): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.actionHistoryCommon.getStpActionHistoryByStpMainId}${stpMainId}/${encodeURIComponent(applicationId)}`,
    );
  }

  readStpDumpInfo(
    stpMainId: number,
    stpMainDumpId: number,
  ): Observable<ServerResponse> {
    return this.httpClient.get<ServerResponse>(
      `${applicationUrls.stpMainDump.readStpMainDumpInfoByIds}${stpMainId}/${stpMainDumpId}`,
    );
  }
}
